import type { ReactNode } from "react";
import { CaretRight, DownloadSimple, FileText, Spinner } from "@phosphor-icons/react";
import { usePortalIdentity } from "../auth/usePortalIdentity";
import {
  CATEGORY_LABEL,
  CATEGORY_NOTE,
  groupCatalogue,
  useDocumentCatalogue,
  useDocumentDownload,
  type CatalogueEntry
} from "../data/documentQueries";
import { summariseDocuments } from "../data/overviewQueries";
import { Badge } from "../ui/Badge";
import { BoxState } from "../ui/BoxState";
import { ClientHead } from "../ui/ClientHead";
import { Hint } from "../ui/Hint";
import { Panel } from "../ui/Panel";
import "./clientOps/clientOps.css";
import { useViewOnly } from "../data/readSource";

/**
 * Dokument, plan.md section 5.11: the client can download the documents that govern the assignment
 * and the management relationship, in exactly the version they accepted. The original channel stays
 * authoritative and the documents are still emailed; the portal is a reliable copy.
 *
 * A replaced version keeps its entry. The version in force is the document's row, and the ones it
 * replaced open under it, each saying the period it applied. That is the reason versions are frozen
 * at all, and the line behind the question mark on the heading is only true because the screen shows
 * it happening.
 *
 * What this view deliberately does not have: an approval control of any kind. A change order is
 * approved in the mail, and the change-order box says so behind its question mark rather than
 * leaving the absence to be guessed at. Nor does it show who approved what, from which address, at
 * which time; that is `private.approval_evidence` and it is Petrovic's record. Nor does it show the
 * SHA-256 checksum or a copy control for it: the checksum is evidence for Petrovic and noise for a
 * client, so it stays on the admin document screens. Decided by Mattej 2026-09-24, docs/polish.md P-10.
 *
 * Drawn after `prototype/kund/pages.js` `documentsPage` in the second build round of 2026-09-25: one
 * box per group, one row per document with its version and its validity under the title and a
 * download button at the end, and the earlier versions behind a disclosure. The introduction and
 * the footnote went (design.md section 3, rule 3); the footnote's point is the heading's Hint. The
 * file's size, type and name went with the sketch too: the download carries the name, and a size
 * beside every agreement is a number nobody acts on.
 */

const dateOnly = new Intl.DateTimeFormat("sv-SE", { dateStyle: "long" });

function formatDay(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? null : dateOnly.format(parsed);
}

/**
 * When the version applied, said the way it happened.
 *
 * A closed period is written as "applied from X, replaced Y" rather than as a range with a dash,
 * because `effective_to` is the day the successor took over and a dash would leave the reader to
 * decide whether the last day counts.
 */
function period(entry: CatalogueEntry): string {
  const from = formatDay(entry.effective_from);
  const to = formatDay(entry.effective_to);

  if (!entry.is_current) {
    if (from && to) {
      return `Gällde från ${from}, ersatt ${to}`;
    }
    return to ? `Ersatt ${to}` : "Tidigare version";
  }

  return from ? `Gäller från ${from}` : `Fryst ${formatDay(entry.frozen_at) ?? ""}`.trim();
}

interface EntryProps {
  entry: CatalogueEntry;
  tenantId: string;
  /** The document's title, which a replaced version's row does not repeat but its button must say. */
  title: string;
  isNew?: boolean;
}

/**
 * One version of one document, with its own download.
 *
 * Each version has its own mutation, so a failure says which file failed and the others stay as
 * they were. The button names the document, because a screen reader moving between six buttons
 * that all say "Ladda ner" has no way to tell them apart.
 */
function Entry({ entry, tenantId, title, isNew = false }: EntryProps) {
  const download = useDocumentDownload();
  const viewOnly = useViewOnly();
  const current = entry.is_current;
  const name = current ? title : `${title}, ${entry.version_label}`;

  const row = (
    <div className="k-doc-row">
      {current ? (
        <span className="type-icon" aria-hidden="true">
          <FileText size={18} />
        </span>
      ) : null}
      <span className="primary-cell">
        {current ? (
          <strong className="k-doc-title">
            {title}
            {isNew ? <Badge tone="info">Nytt</Badge> : null}
          </strong>
        ) : (
          <strong>{entry.version_label}</strong>
        )}
        <span>{current ? `${entry.version_label} · ${period(entry)}` : period(entry)}</span>
      </span>
      {viewOnly ? null : (
      <button
        type="button"
        className={`k-icon-button${download.isPending ? " is-spinning" : ""}`}
        aria-label={download.isPending ? `Hämtar ${name}` : `Ladda ner ${name}`}
        disabled={download.isPending}
        onClick={() =>
          download.mutate({
            tenantId,
            documentVersionId: entry.document_version_id,
            filename: entry.original_filename
          })
        }
      >
        {download.isPending ? (
          <Spinner size={20} aria-hidden="true" />
        ) : (
          <DownloadSimple size={20} aria-hidden="true" />
        )}
      </button>
      )}
    </div>
  );

  const failed = download.isError ? (
    <p className="k-doc-error" role="alert">
      Filen gick inte att hämta. Försök igen.
    </p>
  ) : null;

  return current ? (
    <div data-testid="document-current">
      {row}
      {failed}
    </div>
  ) : (
    <li data-testid="document-historical">
      {row}
      {failed}
    </li>
  );
}

export function DocumentsRoute() {
  const identity = usePortalIdentity();
  const tenantId = identity.data?.tenantId ?? null;
  const catalogue = useDocumentCatalogue(tenantId);

  const page = (content: ReactNode) => (
    <div className="k-page">
      <ClientHead
        title="Dokument"
        actions={<Hint>Varje version är fryst. Det ni laddar ner är samma fil som gällde då.</Hint>}
      />
      {/*
        On the page and not behind the question mark, since 2026-09-26: the mail is the binding
        channel and the shelf is a copy, and that is said in every state, the failing ones included.
      */}
      <p className="quiet" data-testid="documents-by-mail">
        Ni får dem också per mejl, och den vägen gäller.
      </p>
      {content}
    </div>
  );

  const stateBox = (state: ReactNode) => <section className="panel">{state}</section>;

  if (identity.isLoading || catalogue.isLoading) {
    return page(stateBox(<BoxState kind="loading" title="Hämtar dokumenten" />));
  }

  // No tenant is a refusal rather than an error: nothing failed, and there is nothing to retry.
  if (!tenantId) {
    return page(
      stateBox(
        <BoxState kind="denied" title="Den här vyn är inte din" body="Kontot hör inte till någon kund i portalen." />
      )
    );
  }

  if (catalogue.isError) {
    return page(
      stateBox(
        <BoxState
          kind="error"
          title="Dokumenten gick inte att hämta"
          body="Inget har ändrats och inget har försvunnit. Försök igen, och hör av er i chatten om det upprepas."
          onRetry={() => void catalogue.refetch()}
        />
      )
    );
  }

  const entries = catalogue.data ?? [];
  const groups = groupCatalogue(entries);

  if (groups.length === 0) {
    return page(
      stateBox(
        <BoxState
          kind="empty"
          title="Här kommer avtalen att ligga"
          body="Så snart uppdraget är bekräftat lägger vi upp bekräftelsen, villkoren och biträdesavtalet här. Ni får dem per mejl först."
        />
      )
    );
  }

  /*
    "Nytt" is the thirty-day window Översikt counts with, `summariseDocuments`, and not an unread
    state: the catalogue has no per-person read state, and a badge that cleared on one device would
    tell a colleague a document they never opened is old.
  */
  const recent = new Set(summariseDocuments(entries).recent.map((entry) => entry.document_version_id));

  return page(
    <div className="stack">
      {groups.map((group) => (
        <Panel
          key={group.category}
          title={CATEGORY_LABEL[group.category] ?? CATEGORY_LABEL.other}
          titleId={`group-${group.category}`}
          hint={CATEGORY_NOTE[group.category]}
          flush
        >
          <ul className="k-rows">
            {group.threads.map((thread) => {
              const title = thread.current.display_title;

              return (
                <li key={thread.documentId} data-testid="document-thread">
                  <Entry
                    entry={thread.current}
                    tenantId={tenantId}
                    title={title}
                    isNew={recent.has(thread.current.document_version_id)}
                  />

                  {thread.historical.length > 0 ? (
                    <details className="k-open k-older" data-testid="document-history">
                      <summary>
                        <span className="k-caret" aria-hidden="true">
                          <CaretRight size={14} />
                        </span>
                        {thread.historical.length === 1 ? "Tidigare version" : "Tidigare versioner"}
                      </summary>
                      <ul>
                        {thread.historical.map((entry) => (
                          <Entry
                            key={entry.document_version_id}
                            entry={entry}
                            tenantId={tenantId}
                            title={title}
                          />
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Panel>
      ))}
    </div>
  );
}
